import { useFormContext, useFieldArray } from "react-hook-form";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Consultation, HistoryConsultation } from "@/types/consultation";
import { useEffect, useRef, useState } from "react";
import {
  useGetGoalQuery,
  useDeleteGoalMutation,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useGetAllGoalEvaluationsQuery,
  useDeleteGoalEvaluationMutation,
} from "@/services/consultationApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { HistoryFilter } from "../history-filter";
import { LoadingButton } from "@/components/ui/common/loading-button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, Loader2, Plus, Target, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import GoalEvaluationSheet from "./evaluation";
import { toast } from "@/components/ui/use-toast";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const GoalStep = ({ action, open }: FormStepProps) => {
  const { register, watch, control, setValue, getValues } =
    useFormContext<Consultation>();
  const consultationId = watch("id");
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  // Fetch existing goal if in edit mode
  const { data: goalData } = useGetGoalQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || !open },
  );
  
  const { data: evaluationsData } = useGetAllGoalEvaluationsQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || !open || !goalData }
  );

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "goals",
  });

  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  
  const [deleteGoal] = useDeleteGoalMutation();
  const [createGoal] = useCreateGoalMutation();
  const [updateGoal] = useUpdateGoalMutation();
  const [deleteEvaluation] = useDeleteGoalEvaluationMutation();

  const [isEvaluationOpen, setIsEvaluationOpen] = useState(false);
  const [evaluatingGoalId, setEvaluatingGoalId] = useState<number | undefined>(undefined);
  const [itemToDelete, setItemToDelete] = useState<{ index: number; fieldId: string } | null>(null);

  const handleOpenEvaluation = (goalId: number) => {
    setEvaluatingGoalId(goalId);
    setIsEvaluationOpen(true);
  };

  const handleRemove = async (index: number, fieldId: string) => {
    const goal = getValues(`goals.${index}`);

    if (goal?.id) {
      setDeletingIds((prev) => [...prev, fieldId]);
      try {
        // Find if this specific goal has an evaluation
        const evaluationToDelete = evaluationsData?.evaluations?.find(
          (ev) => ev.goal_id === goal.id
        );

        // Delete evaluation first if it exists
        if (evaluationToDelete) {
          await deleteEvaluation({
            consultation_id: Number(consultationId),
            goal_id: goal.id,
            org_id: orgId,
          }).unwrap();
        }

        // Then delete the goal
        await deleteGoal({
          consultation_id: Number(consultationId),
          org_id: orgId,
          goal_id: goal.id,
        }).unwrap();

        const currentFields = getValues("goals") || [];
        const actualIndex = currentFields.findIndex(
          (_, idx) => fields[idx]?.id === fieldId,
        );

        if (actualIndex !== -1) {
          remove(actualIndex);
        } else {
          remove(index);
        }

        toast({
          description: "Goal deleted successfully",
          variant: "success",
        });
      } catch (error: unknown) {
        console.error("Failed to delete goal:", error);
        toast({
          description: "Failed to delete goal. Please try again.",
          variant: "destructive",
        });
      } finally {
        setDeletingIds((prev) => prev.filter((id) => id !== fieldId));
      }
    } else {
      remove(index);
    }
  };

  const handleSaveGoal = async (index: number) => {
    const allFormGoals = getValues("goals") || [];
    const goalToSave = allFormGoals[index];

    if (!goalToSave.goal_text || !goalToSave.days_per_week || !goalToSave.duration_per_day) {
      toast({
        description: "Please fill all required fields for this goal",
        variant: "destructive",
      });
      return;
    }

    if (!consultationId) {
      toast({
        description: "Please save consultation details first",
        variant: "destructive",
      });
      return;
    }

    // Only send the current goal being saved
    const goalsPayload = [goalToSave];

    setSavingIndex(index);
    try {
      let result;
      if (goalToSave.id) {
        result = await updateGoal({ 
          consultation_id: Number(consultationId), 
          org_id: orgId, 
          data: goalsPayload 
        }).unwrap();
      } else {
        result = await createGoal({ 
          consultation_id: Number(consultationId), 
          org_id: orgId, 
          data: goalsPayload 
        }).unwrap();
      }

      // If we got a new ID back, update it in the form state
      if (result?.goal_ids && result.goal_ids.length > 0) {
        setValue(`goals.${index}.id`, result.goal_ids[0]);
      }

      toast({
        description: goalToSave.id ? "Goal updated successfully" : "Goal saved successfully",
        variant: "success",
      });
    } catch (error: unknown) {
      console.error("Failed to save goal:", error);
      toast({
        description: "Failed to save goal",
        variant: "destructive",
      });
    } finally {
      setSavingIndex(null);
    }
  };

  const dataLoaded = useRef(false);

  useEffect(() => {
    if (dataLoaded.current) return;

    if (goalData?.goals && goalData.goals.length > 0) {
      replace(goalData.goals);
      dataLoaded.current = true;
    } else if (
      (!goalData && !consultationId) ||
      (goalData && (!goalData.goals || goalData.goals.length === 0))
    ) {
      // If we have no ID (fresh add) OR we fetched and found no goals, ensure one empty field
      if (fields.length === 0) {
        append({ goal_text: "", days_per_week: 0, duration_per_day: 0 });
      }
      dataLoaded.current = true;
    }
  }, [goalData, replace, append, consultationId, fields.length]);

  const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);
  const memberId = watch("member_id");

  return (
    <div className="space-y-4">
      <HistoryFilter 
        memberId={memberId!} 
        onDataFound={setHistoryData} 
      />

      {historyData && historyData.goals && historyData.goals.length > 0 && (
        <Card className="p-4 border-[#e2e8f0]">
          <div className="flex items-center justify-between mb-3">
             <p className="font-semibold text-sm text-[#1e293b]">Previous Goals</p>
             <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
                Historical View
             </span>
          </div>
          <div className="space-y-3">
             {historyData.goals.map((goal, idx) => (
                <div key={goal.id || idx} className="bg-[#f8fafc] border border-[#f1f5f9] p-3 rounded-lg">
                   <p className="text-xs font-semibold text-[#1e293b] mb-1 italic">"{goal.goal_text}"</p>
                   <div className="flex gap-4 text-[11px] text-[#64748b]">
                      <span className="flex items-center">
                         <span className="font-bold mr-1">Days/Week:</span> {goal.days_per_week}
                      </span>
                      <span className="flex items-center">
                         <span className="font-bold mr-1">Time/Day: </span> {goal.duration_per_day} min
                      </span>
                   </div>
                </div>
             ))}
          </div>
        </Card>
      )}
      {fields.map((field, index) => (
        <Card key={field.id} className="p-4 relative">
          <div className="mb-4 flex justify-between items-start">
            <div>
              <p className="font-medium text-sm">Goal {index + 1}</p>
              <p className="text-xs text-gray-500 mt-1">
                Define the member's fitness goal and commitment.
              </p>
            </div>
            {fields.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                disabled={deletingIds.includes(field.id)}
                className="h-8 w-8 text-destructive hover:text-destructive/90"
                onClick={() => {
                  const goal = getValues(`goals.${index}`);
                  if (goal?.id) {
                    setItemToDelete({ index, fieldId: field.id });
                  } else {
                    handleRemove(index, field.id);
                  }
                }}
              >
                {deletingIds.includes(field.id) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          <Separator className="h-[1px] mb-4" />

          <div className="space-y-4">
            <div className="w-full space-y-2">
              <Label>
                Goal Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Describe your goal..."
                {...register(`goals.${index}.goal_text`)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="w-full space-y-2">
                <Label>
                  Days Per Week <span className="text-red-500">*</span>
                </Label>
                <Input
                  max={7}
                  min={1}
                  type="number"
                  placeholder="e.g. 4"
                  {...register(`goals.${index}.days_per_week`, {
                    valueAsNumber: true,
                  })}
                />
              </div>
              <div className="w-full space-y-2">
                <Label>
                  Time Per Day (Minutes) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  placeholder="e.g. 60"
                  {...register(`goals.${index}.duration_per_day`, {
                    valueAsNumber: true,
                  })}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <LoadingButton
                type="button"
                size="sm"
                className="bg-primary text-white"
                loading={savingIndex === index}
                onClick={() => handleSaveGoal(index)}
              >
                <Check className="mr-2 h-4 w-4" />
                {watch(`goals.${index}.id`) ? "Update Goal" : "Save Goal"}
              </LoadingButton>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!watch(`goals.${index}.id`)}
                onClick={() => {
                  const goalId = watch(`goals.${index}.id`);
                  if (goalId) handleOpenEvaluation(goalId);
                }}
                className="border-primary text-primary hover:bg-primary/5"
              >
                <Target className="mr-2 h-4 w-4" />
                Add Goal Evaluation
              </Button>
              
              {!watch(`goals.${index}.id`) && (
                <p className="text-[10px] text-amber-600 font-medium">
                  * Save goal first to add evaluation
                </p>
              )}
            </div>
          </div>
        </Card>
      ))}

      <Button
        type="button"
        variant="outline"
        className="w-full border-dashed"
        onClick={() =>
          append({ goal_text: "", days_per_week: 0, duration_per_day: 0 })
        }
      >
        <Plus className="mr-2 h-4 w-4" />
        Add Goal
      </Button>

      <GoalEvaluationSheet
        isOpen={isEvaluationOpen}
        onOpenChange={(open) => {
          setIsEvaluationOpen(open);
          if (!open) setEvaluatingGoalId(undefined);
        }}
        data={watch()}
        initialSelectedGoalId={evaluatingGoalId}
      />

      <AlertDialog 
        open={!!itemToDelete} 
        onOpenChange={(open) => !open && setItemToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the goal evaluation (if any) and the goal from the consultation record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (itemToDelete) {
                  handleRemove(itemToDelete.index, itemToDelete.fieldId);
                  setItemToDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GoalStep;
